import type { Ref } from 'vue'

/**
 * Löst einen Kurstyp-Slug (z. B. «smart-home») in den kanonischen Content-Pfad
 * (z. B. «/kurs/smart-home/smart-home») auf.
 *
 * Die `kurse`-Content-Collection ist die Single Source of Truth für Kurs-URLs:
 * Der Pfad ergibt sich aus der Verzeichnisstruktur unter `content/1.kurs/`.
 * Backend-gelieferte URLs (z. B. aus `upcomingEvents.url`) verwenden teils
 * abweichende Kategorie-Slugs (`smarthome` statt `smart-home`) und dürfen
 * deshalb nicht als Link-Ziel genutzt werden.
 */
export function useCourseUrl() {
  const { data: slugMap } = useAsyncData('coursetype-slug-paths', async () => {
    const pages = await queryCollection('kurse')
      .where('slug', 'IS NOT NULL')
      .select('slug', 'path')
      .all()

    const map: Record<string, string> = {}
    for (const page of pages) {
      if (page.slug && page.path) map[page.slug] = page.path
    }
    return map
  }, { default: () => ({} as Record<string, string>) })

  /**
   * Gibt den kanonischen Pfad zu einem Kurstyp-Slug zurück oder `null`,
   * wenn kein passender Content existiert.
   */
  function courseUrl(slug: string | null | undefined): string | null {
    if (!slug) return null
    return slugMap.value?.[slug] ?? null
  }

  return { courseUrl, slugMap: slugMap as Ref<Record<string, string>> }
}
